import { Skeleton, Box, Flex, Text } from '@chakra-ui/react';
import * as _ from 'lodash';
import React, { useState, useMemo } from 'react';

import { PortfolioChartWithTableTemplateForCreditFacility } from '../shared/PortfolioChartTemplate';
import {
  chartsInitialEndDateJs,
  chartsInitialStartDateJs,
  ChartTimePeriod,
  defaultSelectedTimePeriod,
  ExpandedEligibilityAggregateInsightsData,
  expandEligibilityKickoutsData,
  formatChartDataByReportingDate,
  formatCsvDownloadDataRows,
  getInitialActiveFields,
  onToggleActiveField,
  PortfolioChartConfig,
} from '../shared/portfolioChartUtils';
import { PortfolioDownloadCsvLink } from '../shared/PortfolioDownloadCsvLink';
import { PortfolioReChart } from '../shared/PortfolioReChart';
import { generateEligibilityKickoutsChartConfig } from './portfolioBorrowingBaseChartConfigs';
import { PortfolioEligibilityKickoutsChartDetailsTable } from './PortfolioEligibilityKickoutsChartDetailsTable';
import { Props as DefaultLegendContentProps } from 'recharts/types/component/DefaultLegendContent';
import { colors } from '~/packages/common-frontend/colors';
import {
  getDefaultDashboardDisplayEndDate,
  getDefaultDashboardDisplayStartDate,
} from '~/packages/common-frontend/constants';
import { useGetDailyEligibilityInsightsQuery } from '~/packages/portfolio/requests/requests.gql.generated';

// custom legend where we only have one legend item to represent all of the individual eligibility kickouts
const renderCustomLegend = (props: DefaultLegendContentProps) => {
  const { payload } = props;
  const totalEligibilityKickoutPayload = _.find(
    payload,
    (legendItem) => legendItem.value === 'totalNetEligibilityKickout',
  );

  return (
    <Flex>
      <Flex alignItems="center">
        <Box backgroundColor={colors.brick[950]} h="15px" w="20px" />
        <Text
          as="span"
          color="black"
          fontSize="14px"
          fontWeight={500}
          margin="0 40px 0 16px"
        >
          Individual Eligibility Kickouts
        </Text>
      </Flex>
      <Flex alignItems="center">
        <Box
          backgroundColor={
            totalEligibilityKickoutPayload?.color ?? colors.chart.teal
          }
          h="15px"
          w="20px"
        />
        <Text
          as="span"
          color="black"
          fontSize="14px"
          fontWeight={500}
          margin="0 40px 0 16px"
        >
          Total Eligibility Kickout
        </Text>
      </Flex>
    </Flex>
  );
};

type PortfolioEligibilityKickoutsChartProps = {
  creditFacilityId: string;
  chartTitle: string;
};
export const PortfolioEligibilityKickoutsChart = ({
  creditFacilityId,
  chartTitle,
}: PortfolioEligibilityKickoutsChartProps) => {
  const [
    selectedBar,
    setSelectedBar,
  ] = useState<ExpandedEligibilityAggregateInsightsData | null>(null);
  const [chartDataDateRange, setChartDataDateRange] = useState<{
    chartDataStartDate: Date;
    chartDataEndDate: Date;
  }>({
    chartDataStartDate: chartsInitialStartDateJs,
    chartDataEndDate: chartsInitialEndDateJs,
  });

  // need both initial date range chart data (returned by query)
  // as well as chart data for time period
  // because we only want to filter chart data for time period on top of initial date range chart data
  // and not include a previously-filtered time period
  const [chartDataForDateRange, setChartDataForDateRange] = useState<
    ExpandedEligibilityAggregateInsightsData[]
  >([]);
  const [chartDataWithTimePeriod, setChartDataWithTimePeriod] = useState<
    ExpandedEligibilityAggregateInsightsData[]
  >([]);
  const [selectedTimePeriod, setSelectedTimePeriod] = useState<ChartTimePeriod>(
    defaultSelectedTimePeriod,
  );

  const [activeFields, setActiveFields] = useState<
    (keyof ExpandedEligibilityAggregateInsightsData)[]
  >([]);

  const [chartConfig, setChartConfig] = useState<PortfolioChartConfig<string>>(
    {},
  );

  const { dataRows, headers } = useMemo(
    () =>
      formatCsvDownloadDataRows<ExpandedEligibilityAggregateInsightsData>(
        chartDataWithTimePeriod,
        activeFields,
      ),
    [chartDataWithTimePeriod, activeFields],
  );

  const {
    refetch: refetchEligibilityInsights,
    data: eligibilityInsights,
    loading: isEligibilityInsightsLoading,
  } = useGetDailyEligibilityInsightsQuery({
    variables: {
      creditFacilityParams: {
        creditFacilityId,
        reportingDateStart: getDefaultDashboardDisplayStartDate(),
        reportingDateEnd: getDefaultDashboardDisplayEndDate(),
      },
    },
    onCompleted: (data) => {
      const expandedEligibilityChartData = expandEligibilityKickoutsData(
        data.getDailyEligibilityInsights,
      );

      // we must make sure to set the core date range chart data with all of the data by day
      // this ensures that we have the base by-day data ready to use
      // when going back to a higher-frequency time period view than the currently selected period
      const formattedChartData = formatChartDataByReportingDate<ExpandedEligibilityAggregateInsightsData>(
        {
          chartData: expandedEligibilityChartData,
          selectedTimePeriod: ChartTimePeriod.day,
        },
      );
      setChartDataForDateRange(formattedChartData);

      // then set data for the specific time period
      if (selectedTimePeriod !== ChartTimePeriod.day) {
        const formattedChartDataForPeriod = formatChartDataByReportingDate<ExpandedEligibilityAggregateInsightsData>(
          {
            chartData: expandedEligibilityChartData,
            selectedTimePeriod,
          },
        );
        setChartDataWithTimePeriod(formattedChartDataForPeriod);
      } else {
        setChartDataWithTimePeriod(formattedChartData);
      }

      setSelectedBar(formattedChartData[formattedChartData.length - 1]);

      const eligibilityChartConfig = generateEligibilityKickoutsChartConfig(
        formattedChartData,
      );
      setChartConfig(eligibilityChartConfig);

      const initialActiveFields = getInitialActiveFields({
        chartConfig: eligibilityChartConfig,
      }) as (keyof ExpandedEligibilityAggregateInsightsData)[];

      setActiveFields(initialActiveFields);
    },
    notifyOnNetworkStatusChange: true,
  });

  if (isEligibilityInsightsLoading || !eligibilityInsights) {
    return <Skeleton height="700px" />;
  }

  const onSelectTimePeriod = (timePeriod: ChartTimePeriod) => {
    setSelectedTimePeriod(timePeriod);

    const formattedChartData = formatChartDataByReportingDate<ExpandedEligibilityAggregateInsightsData>(
      {
        chartData: chartDataForDateRange,
        selectedTimePeriod: timePeriod,
      },
    );

    setChartDataWithTimePeriod(formattedChartData);
  };

  return (
    <PortfolioChartWithTableTemplateForCreditFacility
      creditFacilityId={creditFacilityId}
      chartTitle={chartTitle}
      refetch={refetchEligibilityInsights}
      chartDataDateRange={chartDataDateRange}
      setChartDataDateRange={setChartDataDateRange}
      selectedTimePeriod={selectedTimePeriod}
      onSelectTimePeriod={onSelectTimePeriod}
    >
      {/* child 1 */}
      <PortfolioReChart
        chartData={chartDataWithTimePeriod}
        selectedTimePeriod={selectedTimePeriod}
        currentSelectedBarReportingDate={
          selectedBar
            ? selectedBar.reportingDate
            : getDefaultDashboardDisplayEndDate()
        }
        onSelectBar={setSelectedBar}
        chartConfig={chartConfig}
        visibleChartComponents={activeFields}
        renderCustomLegend={renderCustomLegend}
      />

      {/* child 2 */}

      {selectedBar && (
        <Box width="100%" mt="64px">
          <PortfolioEligibilityKickoutsChartDetailsTable
            selectedBarData={selectedBar}
            chartConfig={chartConfig}
            chartTitle={chartTitle}
            activeFields={activeFields}
            onToggleActiveField={(selectedField) =>
              onToggleActiveField({
                selectedField,
                currentActiveFields: activeFields,
                setActiveFields,
              })
            }
          />
          <Flex mt="24px" justifyContent="flex-end">
            <PortfolioDownloadCsvLink
              dataRows={dataRows}
              headers={headers}
              chartTitle={chartTitle}
              selectedTimePeriod={selectedTimePeriod}
            />
          </Flex>
        </Box>
      )}
    </PortfolioChartWithTableTemplateForCreditFacility>
  );
};
